import React, { useState, useEffect, useCallback, useRef } from 'react';

// LiveStreamOverlay (WorshipKit #254)
// --------------------------------------------------------------------------
// Full-screen YouTube iframe shown while a configured BillboardLiveStreamSource
// is on air. Mounted by PlaylistSlideshow when the upstream live override
// turns active (via Pusher in #253, or via the state-recovery fetch on
// reconnect). Props carry the override payload from the parent's state so
// that data-* attributes round-trip for inspection / future hooks.
//
// State machine:
//   - 'playing'  iframe is mounted. Default when a videoId is provided.
//   - 'failed'   iframe load fired onError, OR the parent passed a null/empty
//                videoId (worker raced is_live ahead of youtube_live_video_id).
//                The fallback message is shown and we wait for ended to
//                fade out + return to the playlist.
//
// Fade-out:
//   - When `fadeOut` flips to true the overlay's opacity transitions from 1 to 0
//     over 1s. We hook the transitionend event for the opacity property and
//     call onFadeComplete so the parent can clear override state.

function buildEmbedSrc(videoId) {
    // autoplay + mute are required together — browsers block unmuted autoplay
    // on unattended displays. playsinline keeps the player from going to the
    // native fullscreen control on mobile / TV WebViews. rel=0 hides the
    // "related videos" overlay from other channels at end of stream.
    const params = 'autoplay=1&mute=1&playsinline=1&rel=0';
    return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params}`;
}

export default function LiveStreamOverlay({
    videoId,
    sourceId,
    channelId,
    provider,
    fadeOut,
    onFadeComplete,
}) {
    // Initial mode: if no videoId, jump straight to fallback (locked behavior
    // from the QA note on #253 — never construct a broken iframe src).
    const hasVideoId = !!videoId;
    const [mode, setMode] = useState(hasVideoId ? 'playing' : 'failed');
    const iframeRef = useRef(null);

    // If the parent sends in a video id after first mounting with one missing
    // (state-recovery picks it up once the worker catches up), recover.
    useEffect(() => {
        if (hasVideoId && mode === 'failed') {
            setMode('playing');
        } else if (!hasVideoId && mode === 'playing') {
            setMode('failed');
        }
        // mode intentionally not in deps — we only want to react to the
        // upstream videoId changing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    const handleIframeError = useCallback(() => {
        setMode('failed');
    }, []);

    // React 16 does not reliably delegate `onError` for non-media elements
    // (iframe in particular). Attach via native addEventListener so the
    // failure path is actually wired. Cross-origin iframes (YouTube) won't
    // fire `error` in every browser anyway — this is a best-effort hook;
    // the watchdog for "embed silently failed" lives at a higher layer.
    useEffect(() => {
        if (mode !== 'playing') return undefined;
        const el = iframeRef.current;
        if (!el) return undefined;
        el.addEventListener('error', handleIframeError);
        return () => el.removeEventListener('error', handleIframeError);
    }, [mode, handleIframeError]);

    const handleTransitionEnd = useCallback((e) => {
        // Multiple properties can transition on a single element. When a
        // propertyName is present, gate on `opacity` so non-opacity
        // transitions on child elements don't double-fire onFadeComplete.
        // jsdom + RTL omit the propertyName field on synthetic transition
        // events, so the missing-field case must pass through.
        if (e && e.propertyName && e.propertyName !== 'opacity') return;
        if (typeof onFadeComplete === 'function') onFadeComplete();
    }, [onFadeComplete]);

    const rootStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#000',
        zIndex: 99999,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter, sans-serif',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 1s ease-in-out',
    };

    return (
        <div
            data-live-stream-override="true"
            data-source-id={sourceId != null ? String(sourceId) : ''}
            data-video-id={videoId || ''}
            data-channel-id={channelId || ''}
            data-provider={provider || ''}
            style={rootStyle}
            onTransitionEnd={handleTransitionEnd}
        >
            {mode === 'playing' && (
                <iframe
                    ref={iframeRef}
                    title="Live stream"
                    src={buildEmbedSrc(videoId)}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 0,
                    }}
                    frameBorder="0"
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                />
            )}

            {mode === 'failed' && (
                <div
                    style={{
                        textAlign: 'center',
                        padding: 32,
                    }}
                >
                    <div style={{ fontSize: 32, marginBottom: 12 }}>
                        Live stream temporarily unavailable
                    </div>
                    <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>
                        Returning to scheduled content shortly
                    </div>
                </div>
            )}
        </div>
    );
}
